import type { MaybeRef, Ref } from "vue";
import { toValue } from "vue";

export type InputSelection = [start: number, end: number];

export type InputMaskFn = (
  value: string,
  selection: InputSelection,
) => [value: string, selection: InputSelection];

export type InputMaskArray = (string | RegExp)[];

export type ReplaceArgs = [substring: string, ...args: any[]];

export type InputMaskRegex = [
  regex: RegExp,
  replacer: (...args: ReplaceArgs) => string,
];

export type InputMask = InputMaskFn | InputMaskArray | InputMaskRegex | string;

export const stringMaskRegExp: Record<string, RegExp> = {
  9: /\d/,
  0: /\d?/,
  a: /[a-z]/i,
  o: /[a-z]?/i,
  "*": /\w/,
  "?": /\w?/,
};

/** Convert a string mask to an array mask */
export const stringMaskToArray = (mask: string, regexps = stringMaskRegExp) =>
  [...mask].map((c) => regexps[c] || c);

/** Convert a regex mask to a mask function */
export const regexMaskToFn =
  (regex: RegExp, replacer: (...args: ReplaceArgs) => string): InputMaskFn =>
  (value: string, selection: InputSelection) => [
    value.replace(regex, (...args) => {
      const replacement = replacer(...args);
      const index = args[args.length - 2];
      selection[0] +=
        index < selection[0]
          ? 0
          : ((replacement.length - args[0].length) / args[0].length) *
            Math.max(selection[0] - index, args[0].length);
      selection[1] +=
        index < selection[1]
          ? 0
          : ((replacement.length - args[0].length) / args[0].length) *
            Math.max(selection[1] - index, args[0].length);
      return replacement;
    }),
    selection,
  ];

/** Convert an array mask to a mask function */
export const maskArrayToFn =
  (maskArray: InputMaskArray): InputMaskFn =>
  (value: string, selection: InputSelection) => {
    let pos = 0;
    maskArray.forEach((maskItem) => {
      if (value.length >= pos + 1) {
        if (typeof maskItem === "string") {
          const index = value.slice(pos).indexOf(maskItem);
          if (index !== 0) {
            value = value.slice(0, pos) + maskItem + value.slice(pos);
            selection[0] +=
              ((selection[0] > pos) as unknown as number) * maskItem.length;
            selection[1] +=
              ((selection[1] > pos) as unknown as number) * maskItem.length;
          }
          pos += maskItem.length;
          return;
        }
        const match = value.slice(pos).match(maskItem);
        if (!match || match.index === undefined) {
          value = value.slice(0, pos);
          return;
        } else if (match.index > 0) {
          value = value.slice(0, pos) + value.slice(pos + match.index);
          pos -= match.index - 1;
          selection[0] -=
            ((selection[0] > pos) as unknown as number) * match.index;
          selection[1] -=
            ((selection[1] > pos) as unknown as number) * match.index;
        }
        pos += match[0].length;
      }
    });
    return [value.slice(0, pos), selection];
  };

/** Convert an array or string mask to a mask function */
export const anyMaskToFn = (
  mask: InputMask,
  regexps?: Record<string, RegExp>,
) =>
  typeof mask === "function"
    ? mask
    : typeof mask[1] === "function" && mask[0] instanceof RegExp
      ? regexMaskToFn(mask[0], mask[1])
      : maskArrayToFn(
          Array.isArray(mask)
            ? (mask as InputMaskArray)
            : stringMaskToArray(mask, regexps),
        );

export interface EventLike {
  target: EventTarget | null;
  currentTarget: EventTarget | null;
}

export interface InputLike {
  value: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  setSelectionRange: (start: number, end: number) => void;
}

/**
 * Adds a data-attributes with value and remaining pattern to be used in ::before/::after
 * > Requires an extra empty element (e.g. label) directly before the input
 * ### CSS:
 * ```css
 * label[data-mask-value] {
 *
 * }
 * label[data-mask-value]::before {
 *   content: attr(data-mask-value);
 *   color: transparent;
 * }
 * label[data-mask-pattern]::after {
 *   content: attr(data-mask-pattern);
 *   opacity: 0.7;
 * }
 * ```
 *
 * ### JSX/TSX:
 * ```tsx
 * <div>
 *   <label></label>
 *   <input
 *     placeholder="YYYY-MM-DD"
 *     onInput={createMaskPattern(createInputMask("9999-99-99"), () => "YYYY-MM-DD")}
 *   />
 * </div>
 * ```
 */
export const createMaskPattern = <
  MaskEvent extends EventLike = KeyboardEvent | InputEvent | ClipboardEvent,
>(
  inputMask: (ev: MaskEvent) => string,
  pattern: (value?: string) => string,
): ((ev: MaskEvent) => string) => {
  const handler = (ev: MaskEvent) => {
    const value = inputMask(ev);
    const ref = (ev.currentTarget || ev.target) as
      | HTMLInputElement
      | HTMLTextAreaElement;
    const prev = ref.previousElementSibling as HTMLElement;
    const fn = value === "" ? "removeAttribute" : "setAttribute";
    prev[fn]("data-mask-value", value);
    prev[fn]("data-mask-pattern", pattern(value).slice(value.length));
    return value;
  };
  return handler;
};

/**
 * Create input mask handler
 * ```tsx
 * <input onInput={createInputMask("9999-99-99")} />
 * ```
 */
export const createInputMask = <
  Element extends InputLike = HTMLInputElement,
  MaskEvent extends EventLike = Event,
>(
  ref: Ref<Element | null>,
  mask: InputMask,
  regexps?: Record<string, RegExp>,
): ((ev: MaskEvent) => string) => {
  const maskFn = anyMaskToFn(mask, regexps);
  const handler = () => {
    const el = ref.value;
    if (!el) {
      throw new Error("Input element not found, unable to mask value");
    }

    const [value, selection] = maskFn(el.value, [
      el.selectionStart || el.value.length,
      el.selectionEnd || el.value.length,
    ]);
    el.value = value;
    el.setSelectionRange(...selection);
    return value;
  };
  return handler;
};

export function getInputSelection<Element extends InputLike = HTMLInputElement>(
  ref: MaybeRef<Element | null>,
): InputSelection {
  const el = toValue(ref);
  if (!el) {
    throw new Error("Input element not found, unable to resolve selection");
  }
  return [
    el.selectionStart || el.value.length,
    el.selectionEnd || el.value.length,
  ];
}

export function useInputMask<Element extends InputLike = HTMLInputElement>(
  ref: Ref<Element | null>,
  valueRef: Ref<string | undefined>,
  mask: InputMask,
) {
  const maskFn = anyMaskToFn(mask);

  function handler(v: string) {
    const el = ref.value;
    if (!el) {
      throw new Error("Input element not found, unable to mask value");
    }

    const s = getInputSelection(ref);
    const [value, selection] = maskFn(v, [s[0] || v.length, s[1] || v.length]);
    el.value = value;
    el.setSelectionRange(...selection);
    valueRef.value = value;
    return value;
  }

  function onChange() {
    const el = ref.value;
    if (!el) {
      throw new Error("Input element not found, unable to mask value");
    }

    return handler(el.value);
  }

  return {
    maskHandler: handler,
    onChange,
  };
}
